import { useEffect, useState } from "react";
import { Table, Button, Modal, Input, Switch, message, Popconfirm } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DeleteFilled, EyeFilled } from "@ant-design/icons";
import "./templateTable.css";
import { serverAddress } from "../constant";
import { useFormik } from "formik";
import { notify_error, notify_success } from "./Notify";
import * as Yup from "yup";

const TemplatesTable = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce searchQuery and update debouncedQuery after 400ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 400); // adjust debounce delay as needed

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch when debouncedQuery changes
  useEffect(() => {
    if (!debouncedQuery) {
      fetchTemplates(); // or optionally fetch default list here
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${serverAddress}/template/search?query=${encodeURIComponent(debouncedQuery)}`);
        const data = await response.json();
        setTemplates(data);
      } catch (err) {
        console.error("Search failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${serverAddress}/template/get-all`);
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const columns = [
    {
      title: "S.No.",
      key: "sno",
      render: (_, __, index) => index + 1,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#f2f5f7",
          fontSize: "16px",
          fontWeight: "bold",
        },
      }),
      onCell: () => ({
        style: {
          fontSize: "15px",
        },
      }),
    },
    {
      title: "Template Name",
      dataIndex: "template_name",
      key: "name",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#f2f5f7",
          fontSize: "16px",
          fontWeight: "bold",
        },
      }),
      onCell: () => ({
        style: {
          fontSize: "15px",
        },
      }),
    },
    {
      title: "Template ID",
      dataIndex: "template_sId",
      key: "template_sId",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#f2f5f7",
          fontSize: "16px",
          fontWeight: "bold",
        },
      }),
      onCell: () => ({
        style: {
          fontSize: "15px",
        },
      }),
    },
    {
      title: "Dynamic",
      dataIndex: "dynamic",
      key: "dynamic",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#f2f5f7",
          fontSize: "16px",
          fontWeight: "bold",
        },
      }),
      onCell: () => ({
        style: {
          fontSize: "15px",
        },
      }),
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#f2f5f7",
          fontSize: "16px",
          fontWeight: "bold",
        },
      }),
      onCell: () => ({
        style: {
          fontSize: "15px",
          fontWeight: 500,
        },
      }),
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            style={{ border: "none", backgroundColor: "white" }}
            icon={<EyeFilled style={{ color: "blue" }} />}
            onClick={() => {
              setSelectedTemplate(record);
              setIsViewModalOpen(true);
            }}
          ></Button>
          <Popconfirm
            title="Are you sure to delete this template?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              style={{ border: "none", backgroundColor: "white" }}
              icon={<DeleteFilled style={{ color: "red" }} />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddTemplate = () => {
    setIsAddModalOpen(true);
    formik.resetForm();
    setEditorContent("");
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${serverAddress}/template/delete/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Delete failed");

      message.success("Template deleted successfully");
      fetchTemplates();
    } catch (err) {
      message.error("Failed to delete template");
      console.error(err);
    }
  };

  const formik = useFormik({
    initialValues: {
      template_name: "",
      template_sId: "",
      dynamic: false,
      content: "",
    },
    validationSchema: Yup.object({
      template_name: Yup.string().required("Template name is required"),
      template_sId: Yup.string().required("Template ID is required"),
      content: Yup.string().required("Content is required"),
    }),
    onSubmit: async (values) => {
      try {
        const response = await fetch(`${serverAddress}/template/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });

        if (!response.ok) throw new Error("Failed to create template");

        notify_success("Template created successfully");
        setIsAddModalOpen(false);
        fetchTemplates();
      } catch (err) {
        console.error(err);
        notify_error("Failed to create template");
      }
    },
  });

  return (
    <>
      <div className="main-panel mt-5">
        <div className="content-wrapper">
          <div
            style={{
              display: "flex",
              background: "white",
              padding: "8px",
              justifyContent: "space-between",
              alignItems: "end",
              marginBottom: 16,
            }}
          >
            <Input
              placeholder="Search by Template Name or ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: 300,
                height: "40px",
                borderRadius: "8px",
                marginRight: 8,
                marginBottom: "4px",
              }}
            />
            <Button
              type="primary"
              style={{
                backgroundColor: "#663399",
                color: "#fff",
                borderColor: "#663399",
                width: "auto",
                padding: "8px 12px",
                height: "38px",
                whiteSpace: "nowrap",
                borderRadius: "8px",
                transition: "none",
                marginTop: "2px",
                marginBottom: "2px",
                fontWeight: "700",
              }}
              onClick={handleAddTemplate}
            >
              Add New Template
            </Button>
          </div>

          <Table
            style={{ border: "none", background: "white" }}
            className="no-border-table"
            dataSource={templates}
            columns={columns}
            rowKey="template_sId"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </div>

        <Modal
          title="Add New Template"
          open={isAddModalOpen}
          onCancel={() => setIsAddModalOpen(false)}
          footer={null}
          destroyOnClose
          centered
        >
          <form onSubmit={formik.handleSubmit}>
            <div className="form-group">
              <label htmlFor="template_name">
                Template Name <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                id="template_name"
                name="template_name"
                placeholder="Enter template name"
                value={formik.values.template_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.template_name && formik.errors.template_name && (
                <div className="error">{formik.errors.template_name}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="template_sId">
                Template ID <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                id="template_sId"
                name="template_sId"
                placeholder="Enter template ID"
                value={formik.values.template_sId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.template_sId && formik.errors.template_sId && (
                <div className="error">{formik.errors.template_sId}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="dynamic">Dynamic</label>
              <div style={{ marginTop: "2px" }}>
                <Switch
                  id="dynamic"
                  name="dynamic"
                  checked={formik.values.dynamic}
                  onChange={(checked) => formik.setFieldValue("dynamic", checked)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="content">
                Content <span style={{ color: "red" }}>*</span>
              </label>
              <ReactQuill
                theme="snow"
                value={editorContent}
                onChange={(value) => {
                  setEditorContent(value);
                  formik.setFieldValue("content", value);
                }}
                placeholder="Write your template here..."
                style={{
                  height: "200px",
                  marginBottom: "10px",
                 // border: "1px solid",
                }}
              />
             
            </div>
            <br></br>
            {formik.touched.content && formik.errors.content && (
                <div className="error">{formik.errors.content}</div>
              )}

            <button className="spButton1 py-1 px-3 me-2" type="primary" style={{marginTop:"4px",}} htmlType="submit" block>
              Create Template
            </button>
          </form>
        </Modal>

        <Modal
          title="Template Content"
          open={isViewModalOpen}
          onCancel={() => setIsViewModalOpen(false)}
          footer={null}
          width={800}
          centered
        >
          {selectedTemplate && (
            <div
              style={{
                maxHeight: "600px",
                overflowY: "auto",
                padding: "12px",
              }}
            >
              <p>
                <strong>Template Name:</strong> {selectedTemplate.template_name}
              </p>
              <p>
                <strong>Template ID:</strong> {selectedTemplate.template_sId}
              </p>
              <p>
                <strong>Content:</strong>
              </p>
              <div
                style={{
                  background: "#f9f9f9",
                  padding: "16px",
                  borderRadius: "8px",
                  marginTop: "16px",
                }}
                dangerouslySetInnerHTML={{ __html: selectedTemplate.content }}
              />
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default TemplatesTable;
